/**
 * Client-side geometry file parser, shared by the admin frontend and the Atlas
 * client app via @core. Supports .geojson/.json, .kml, .kmz, .shp (as .zip),
 * and .dxf. All outputs are in EPSG:4326.
 *
 * Framework-free (no React). Its npm deps (shpjs, @tmcw/togeojson, jszip,
 * proj4, dxf-parser, @turf/area) must be installed in the CONSUMING app's
 * node_modules, since client-core is consumed as raw source.
 */
import { kml } from '@tmcw/togeojson'
import JSZip from 'jszip'
import shp from 'shpjs'
import proj4 from 'proj4'
import area from '@turf/area'
import DxfParser from 'dxf-parser'

// Register common Portuguese CRS definitions
proj4.defs('EPSG:3763', '+proj=tmerc +lat_0=39.6682583333333 +lon_0=-8.13310833333333 +k=1 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
proj4.defs('EPSG:27493', '+proj=tmerc +lat_0=39.6666666666667 +lon_0=-8.13190611111111 +k=1 +x_0=180.598 +y_0=-86.990 +ellps=intl +towgs84=-223.237,110.193,36.649,0,0,0,0 +units=m +no_defs')
proj4.defs('EPSG:20790', '+proj=tmerc +lat_0=39.6666666666667 +lon_0=1 +k=1 +x_0=200000 +y_0=300000 +ellps=intl +towgs84=-304.046,-60.576,103.640,0,0,0,0 +units=m +no_defs')

export interface ParseResult {
  polygon: GeoJSON.Polygon
  bbox: [number, number, number, number] // [minLng, minLat, maxLng, maxLat]
  areaHa: number
  crs?: string
  /** Total polygon candidates found in the file. >1 means we chose the largest. */
  polygonCount: number
}

export async function parseGeometryFile(file: File): Promise<ParseResult> {
  const ext = getExtension(file.name)

  switch (ext) {
    case 'geojson':
    case 'json':
      return parseGeoJSON(file)
    case 'kml':
      return parseKML(file)
    case 'kmz':
      return parseKMZ(file)
    case 'zip':
      return parseShapefile(file)
    case 'shp':
      throw new Error('Ficheiro .shp deve ser enviado como .zip (incluindo .dbf e .prj)')
    case 'dxf':
      return parseDXF(file)
    case 'dwg':
      throw new Error('Formato .dwg nao e suportado no browser. Converta para .dxf no AutoCAD e tente novamente.')
    default:
      throw new Error(`Formato nao suportado: .${ext}`)
  }
}

// ---------------------------------------------------------------------------
// Format parsers
// ---------------------------------------------------------------------------

async function parseGeoJSON(file: File): Promise<ParseResult> {
  const text = await file.text()
  let geojson: unknown
  try {
    geojson = JSON.parse(text)
  } catch {
    throw new Error('Ficheiro GeoJSON invalido . erro de JSON')
  }

  const fc = normalizeToFeatureCollection(geojson as GeoJSON.GeoJSON)
  const { polygon, count } = extractPolygon(fc)
  return finalize(polygon, count)
}

async function parseKML(file: File): Promise<ParseResult> {
  const text = await file.text()
  const doc = new DOMParser().parseFromString(text, 'application/xml')

  const parseError = doc.querySelector('parsererror')
  if (parseError) {
    throw new Error('Ficheiro KML invalido . erro de XML')
  }

  const fc = kml(doc) as GeoJSON.FeatureCollection
  const { polygon, count } = extractPolygon(fc)
  return finalize(polygon, count)
}

async function parseKMZ(file: File): Promise<ParseResult> {
  const buffer = await file.arrayBuffer()
  const zip = await JSZip.loadAsync(buffer)

  // Find the doc.kml (or any .kml file)
  let kmlText: string | null = null
  for (const [name, zipEntry] of Object.entries(zip.files) as [string, { dir: boolean; async: (type: string) => Promise<string> }][]) {
    if (name.toLowerCase().endsWith('.kml') && !zipEntry.dir) {
      kmlText = await zipEntry.async('string')
      break
    }
  }

  if (!kmlText) {
    throw new Error('Ficheiro KMZ nao contem nenhum ficheiro .kml')
  }

  const doc = new DOMParser().parseFromString(kmlText, 'application/xml')
  const fc = kml(doc) as GeoJSON.FeatureCollection
  const { polygon, count } = extractPolygon(fc)
  return finalize(polygon, count)
}

async function parseShapefile(file: File): Promise<ParseResult> {
  const buffer = await file.arrayBuffer()

  let geojson: GeoJSON.FeatureCollection
  try {
    const result = await shp(buffer)
    // shpjs can return a single FeatureCollection or an array of them
    geojson = Array.isArray(result) ? result[0] : result
  } catch {
    throw new Error('Erro ao processar shapefile. Verifique que o .zip contem .shp, .dbf e .prj')
  }

  if (!geojson || !geojson.features || geojson.features.length === 0) {
    throw new Error('Shapefile vazio . nenhuma geometria encontrada')
  }

  const { polygon, count } = extractPolygon(geojson)

  // shpjs doesn't reproject . detect if coords are projected (not WGS84)
  const detectedCrs = detectProjectedCRS(polygon)
  if (detectedCrs) {
    const reprojected = reprojectPolygon(polygon, detectedCrs)
    return finalize(reprojected, count, detectedCrs)
  }

  return finalize(polygon, count)
}

async function parseDXF(file: File): Promise<ParseResult> {
  const text = await file.text()
  const parser = new DxfParser()

  const dxf = parser.parseSync(text)

  if (!dxf || !dxf.entities || dxf.entities.length === 0) {
    throw new Error('Ficheiro DXF vazio . nenhuma entidade encontrada')
  }

  type DxfEntity = (typeof dxf.entities)[number]

  // Find closed polylines (LWPOLYLINE or POLYLINE)
  const hasVertices = (e: DxfEntity): e is DxfEntity & { vertices: Array<{ x: number; y: number }> } =>
    'vertices' in e && Array.isArray(e.vertices) && e.vertices.length >= 3

  const closedPolylines = dxf.entities.filter(
    (e: DxfEntity) =>
      (e.type === 'LWPOLYLINE' || e.type === 'POLYLINE') &&
      hasVertices(e) &&
      ('shape' in e && e.shape === true)
  )

  // If no explicitly closed polylines, try any polyline with 3+ vertices
  const candidates = closedPolylines.length > 0
    ? closedPolylines
    : dxf.entities.filter(
        (e: DxfEntity) =>
          (e.type === 'LWPOLYLINE' || e.type === 'POLYLINE') &&
          hasVertices(e)
      )

  if (candidates.length === 0) {
    throw new Error('Nenhum poligono (polyline fechada) encontrado no ficheiro DXF')
  }

  // Take the largest by vertex count
  let best = candidates[0]
  for (let i = 1; i < candidates.length; i++) {
    const current = candidates[i]
    if (hasVertices(current) && hasVertices(best) && current.vertices.length > best.vertices.length) {
      best = current
    }
  }

  if (!hasVertices(best)) {
    throw new Error('Nenhum poligono valido encontrado no ficheiro DXF')
  }

  const coords: [number, number][] = best.vertices.map((v) => [v.x, v.y])
  // Close the ring if not already closed
  const first = coords[0]
  const last = coords[coords.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coords.push([first[0], first[1]])
  }

  const polygon: GeoJSON.Polygon = {
    type: 'Polygon',
    coordinates: [coords],
  }

  // DXF coordinates are almost always in a projected CRS
  const detectedCrs = detectProjectedCRS(polygon)
  if (detectedCrs) {
    const reprojected = reprojectPolygon(polygon, detectedCrs)
    return finalize(reprojected, candidates.length, detectedCrs)
  }

  return finalize(polygon, candidates.length)
}

// ---------------------------------------------------------------------------
// Geometry extraction helpers
// ---------------------------------------------------------------------------

function normalizeToFeatureCollection(geojson: GeoJSON.GeoJSON): GeoJSON.FeatureCollection {
  if (geojson.type === 'FeatureCollection') {
    return geojson
  }
  if (geojson.type === 'Feature') {
    return { type: 'FeatureCollection', features: [geojson] }
  }
  // Raw geometry
  return {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', properties: {}, geometry: geojson as GeoJSON.Geometry }],
  }
}

/**
 * Choose the polygon to order and report how many polygon candidates the file
 * held (so the caller can warn when it had more than one).
 * Preference: first Polygon -> largest MultiPolygon ring -> first LineString.
 */
function extractPolygon(fc: GeoJSON.FeatureCollection): { polygon: GeoJSON.Polygon; count: number } {
  // Count every polygonal candidate across all features.
  let count = 0
  for (const feature of fc.features) {
    const g = feature.geometry
    if (!g) continue
    if (g.type === 'Polygon') count += 1
    else if (g.type === 'MultiPolygon') count += g.coordinates.length
    else if (g.type === 'LineString' && g.coordinates.length >= 3) count += 1
  }

  // First pass: look for Polygon features
  for (const feature of fc.features) {
    if (feature.geometry.type === 'Polygon') {
      return { polygon: feature.geometry, count }
    }
  }

  // Second pass: look for MultiPolygon, take the largest polygon ring
  for (const feature of fc.features) {
    if (feature.geometry.type === 'MultiPolygon') {
      const polygons = feature.geometry.coordinates
      let bestIdx = 0
      let bestArea = 0
      for (let i = 0; i < polygons.length; i++) {
        const a = Math.abs(ringArea(polygons[i][0]))
        if (a > bestArea) {
          bestArea = a
          bestIdx = i
        }
      }
      return { polygon: { type: 'Polygon', coordinates: polygons[bestIdx] }, count }
    }
  }

  // Third pass: try LineString . close it if it has 3+ points
  for (const feature of fc.features) {
    if (feature.geometry.type === 'LineString' && feature.geometry.coordinates.length >= 3) {
      const coords = [...feature.geometry.coordinates]
      const first = coords[0]
      const last = coords[coords.length - 1]
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push(first)
      }
      return { polygon: { type: 'Polygon', coordinates: [coords] }, count }
    }
  }

  throw new Error('Nenhum poligono encontrado no ficheiro')
}

/** Signed area of a ring using the shoelace formula (for comparison only) */
function ringArea(ring: number[][]): number {
  let sum = 0
  for (let i = 0; i < ring.length - 1; i++) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1]
  }
  return sum / 2
}

// ---------------------------------------------------------------------------
// CRS detection and reprojection
// ---------------------------------------------------------------------------

/**
 * Heuristic CRS detection based on coordinate ranges.
 * Returns the EPSG code if coordinates appear to be in a projected CRS, null if likely WGS84.
 */
function detectProjectedCRS(polygon: GeoJSON.Polygon): string | null {
  const ring = polygon.coordinates[0]
  const xs = ring.map((c) => c[0])
  const ys = ring.map((c) => c[1])
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  // WGS84 range check: longitude -180..180, latitude -90..90
  if (minX >= -180 && maxX <= 180 && minY >= -90 && maxY <= 90) {
    return null
  }

  // EPSG:3763 (PT-TM06): x roughly -200k..300k, y roughly -300k..400k
  if (minX >= -300000 && maxX <= 400000 && minY >= -400000 && maxY <= 500000) {
    return 'EPSG:3763'
  }

  // EPSG:27493 (Datum 73): x roughly 0..400k, y roughly -400k..400k
  if (minX >= -50000 && maxX <= 500000 && minY >= -400000 && maxY <= 400000) {
    return 'EPSG:27493'
  }

  // Generic large coordinates . assume EPSG:3763 as most common Portuguese projection
  if (Math.abs(minX) > 180 || Math.abs(minY) > 90) {
    return 'EPSG:3763'
  }

  return null
}

function reprojectPolygon(polygon: GeoJSON.Polygon, fromCrs: string): GeoJSON.Polygon {
  return {
    type: 'Polygon',
    coordinates: polygon.coordinates.map((ring) =>
      ring.map((coord) => {
        const [lng, lat] = proj4(fromCrs, 'EPSG:4326', [coord[0], coord[1]])
        return [lng, lat]
      })
    ),
  }
}

// ---------------------------------------------------------------------------
// Finalization . bbox, area, validation
// ---------------------------------------------------------------------------

function finalize(polygon: GeoJSON.Polygon, polygonCount: number, crs?: string): ParseResult {
  // Validate ring
  const ring = polygon.coordinates[0]
  if (!ring || ring.length < 4) {
    throw new Error('Poligono invalido . necessita de pelo menos 3 vertices')
  }

  // Ensure ring is closed
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]])
  }

  // Compute bbox
  const xs = ring.map((c) => c[0])
  const ys = ring.map((c) => c[1])
  const bbox: [number, number, number, number] = [
    Math.min(...xs),
    Math.min(...ys),
    Math.max(...xs),
    Math.max(...ys),
  ]

  // Sanity check . are coordinates in plausible WGS84 range?
  if (bbox[0] < -180 || bbox[2] > 180 || bbox[1] < -90 || bbox[3] > 90) {
    throw new Error('Coordenadas fora do intervalo WGS84. Verifique o sistema de coordenadas do ficheiro.')
  }

  // Compute area in hectares using turf
  const feature: GeoJSON.Feature<GeoJSON.Polygon> = {
    type: 'Feature',
    properties: {},
    geometry: polygon,
  }
  const areaSqM = area(feature)
  const areaHa = areaSqM / 10000

  return { polygon, bbox, areaHa, crs, polygonCount }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function getExtension(filename: string): string {
  const parts = filename.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}
